import { useTranslation } from 'react-i18next';
import { type Department } from '@bliks-io/contracts';
import { Badge, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@bliks-io/ui';

export interface ManagedDepartmentSelectorProps {
  /**
   * List of departments the user manages
   */
  departments: Department[];

  /**
   * Currently selected department ID
   */
  selectedDepartmentId: string | undefined;

  /**
   * Callback when department selection changes
   */
  onDepartmentChange: (departmentId: string) => void;

  /**
   * Optional className for the trigger
   */
  className?: string;
}

/**
 * Dropdown selector for switching between managed departments.
 * Only renders if there are 2+ departments to choose from.
 *
 * @example
 * ```tsx
 * const managedDepartments = roleAccess.getManagedDepartments();
 * const [selectedDeptId, setSelectedDeptId] = useState(managedDepartments[0]?._id);
 *
 * <ManagedDepartmentSelector
 *   departments={managedDepartments}
 *   selectedDepartmentId={selectedDeptId}
 *   onDepartmentChange={setSelectedDeptId}
 * />
 * ```
 */
export function ManagedDepartmentSelector({
  departments,
  selectedDepartmentId,
  onDepartmentChange,
  className
}: ManagedDepartmentSelectorProps) {
  const { t } = useTranslation();

  // Don't render if user manages less than 2 departments
  if (departments.length < 2) {
    return null;
  }

  const selectedDepartment = departments.find((d) => d._id === selectedDepartmentId);

  return (
    <Select value={selectedDepartmentId} onValueChange={onDepartmentChange}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={t('department.selector.placeholder')}>
          {selectedDepartment && (
            <div className="flex items-center gap-2">
              <span>{selectedDepartment.name}</span>
              {selectedDepartment.abbreviation && (
                <Badge variant="secondary" className="text-white">
                  {selectedDepartment.abbreviation}
                </Badge>
              )}
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {departments.map((dept) => (
          <SelectItem key={dept._id} value={dept._id}>
            <div className="flex items-center gap-2">
              <span>{dept.name}</span>
              {dept.abbreviation && (
                <Badge variant="secondary" className="text-white">
                  {dept.abbreviation}
                </Badge>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
